import { useEffect, useState, useCallback, useMemo, useRef } from "react";
import {
  getPrivateSessionState,
  subscribePrivateSession,
} from "@/lib/api/private-session";
import type { Timestamp } from "firebase/firestore";
import {
  listenSellerNotificationFeed,
  markSellerNotificationsRead,
} from "../services/seller-notification-feed";
import type {
  AppNotification,
  NotificationFeedSnapshot,
} from "../types/notification-feed.types";
export type { AppNotification } from "../types/notification-feed.types";

export function useNotifications(
  sellerId: string | undefined,
  storeId: string | undefined,
  onNewOrder?: () => void,
) {
  const onNewOrderRef = useRef(onNewOrder);
  useEffect(() => {
    onNewOrderRef.current = onNewOrder;
  }, [onNewOrder]);
  const scope = `${sellerId ?? ""}:${storeId ?? ""}`;
  const [feed, setFeed] = useState<{
    scope: typeof scope;
    items: AppNotification[];
  }>({ scope, items: [] });
  const notifications = useMemo(
    () => (feed.scope === scope ? feed.items : []),
    [feed, scope],
  );

  useEffect(() => {
    // Scope the feed to the active store: a seller only sees notifications for
    // the store they're currently in. Requires both the seller and the active
    // store; resubscribes when the active store changes.
    if (!sellerId || !storeId) {
      return;
    }
    const generation = getPrivateSessionState().generation;
    if (getPrivateSessionState().changing) return;
    let active = true;
    const ownsSession = () =>
      active &&
      !getPrivateSessionState().changing &&
      getPrivateSessionState().generation === generation;
    let serverBaselineReady = false;
    let historyCutoff: Timestamp | null = null;
    const history = new Set<string>();
    const seen = new Set<string>();
    let unsubscribe: (() => void) | undefined;
    const eventKey = (item: AppNotification) =>
      `${item.type}:${item.data?.orderId || item.id}`;
    const baseline = (items: AppNotification[]) => {
      // At most the query's 50 historical identities stay pinned, including
      // null timestamps, so recent-event eviction cannot replay that history.
      for (const item of items) {
        history.add(eventKey(item));
        const createdAt = item.createdAt;
        if (
          createdAt &&
          Number.isFinite(createdAt.toMillis()) &&
          (!historyCutoff ||
            createdAt.seconds > historyCutoff.seconds ||
            (createdAt.seconds === historyCutoff.seconds &&
              createdAt.nanoseconds > historyCutoff.nanoseconds))
        ) {
          historyCutoff = createdAt;
        }
      }
      serverBaselineReady = true;
    };
    const subscribe = () => {
      unsubscribe = listenSellerNotificationFeed(
        (snapshot: NotificationFeedSnapshot) => {
          if (!ownsSession()) return;
          const items = snapshot.items;
          setFeed({ scope, items });
          if (!serverBaselineReady) {
            // If the initial read failed, recover the baseline silently here.
            baseline(items);
            return;
          }
          for (const item of items) {
            const key = eventKey(item);
            if (history.has(key)) continue;
            const createdAt = item.createdAt;
            if (!createdAt || !Number.isFinite(createdAt.toMillis())) continue;
            const fresh = !seen.has(key);
            seen.add(key);
            if (seen.size > 256) seen.delete(seen.values().next().value!);
            if (
              fresh &&
              !item.read &&
              item.type === "order.created" &&
              (!item.data?.storeId || item.data.storeId === storeId) &&
              (historyCutoff === null ||
                createdAt.seconds > historyCutoff.seconds ||
                (createdAt.seconds === historyCutoff.seconds &&
                  createdAt.nanoseconds > historyCutoff.nanoseconds))
            ) {
              onNewOrderRef.current?.();
            }
          }
        },
        (error) => {
          if (active) setFeed({ scope, items: [] });
          console.warn("Notification listener error", error);
        },
        generation,
      );
    };
    const stopForSessionChange = subscribePrivateSession(() => {
      if (!ownsSession()) {
        unsubscribe?.();
        if (active) setFeed({ scope, items: [] });
      }
    });
    // The subscription owns the first server snapshot and sound baseline.
    // It is attached before reading, so in-flight revisions cannot be lost.
    subscribe();

    return () => {
      active = false;
      stopForSessionChange();
      unsubscribe?.();
    };
  }, [sellerId, storeId, scope]);

  const notificationsRef = useRef(notifications);
  useEffect(() => {
    notificationsRef.current = notifications;
  }, [notifications]);

  const unreadCount = notifications.filter((n) => !n.read).length;

  const markAsRead = useCallback(async (notificationId: string) => {
    try {
      await markSellerNotificationsRead([notificationId]);
    } catch (error) {
      console.warn("Failed to mark notification as read", error);
    }
  }, []);

  const markAllAsRead = useCallback(async () => {
    const unread = notificationsRef.current
      .filter((n) => !n.read)
      .map((n) => n.id);
    if (!unread.length) return;
    try {
      await markSellerNotificationsRead(unread);
    } catch (error) {
      console.warn("Failed to mark all notifications as read", error);
    }
  }, []);

  return { notifications, unreadCount, markAsRead, markAllAsRead };
}
